import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSessionStorageItem } from "presentation/utils/sessionStorage";
import {
  DataTable,
  DataTableFilterEvent,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import Spinner from "presentation/components/Shared/Spinner"; // ⬅ ajuste
import {
  translateRegexMessage,
  translateText,
} from "infra/translation/translate";

import { useFilterTechStore } from "presentation/hooks/use-filter-tech-store";

import { useQueryClient } from "@tanstack/react-query";
import { httpClient } from "infra/axios/axios-api";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { Page, PAGE_TYPES } from "domain/entities/Page";
import {
  useCreatePage,
  useDeletePage,
  usePages,
  useUpdatePage,
} from "presentation/hooks/use-query-website";

// aliases/slug — acompanha seu padrão
const alias: Record<string, string> = {
  "c#": "csharp",
  "c++": "cpp",
  ".net": "dotnet",
  node: "node",
  javascript: "javascript",
};
const toSlug = (name: string) => {
  const key = (name ?? "").trim().toLowerCase();
  return alias[key] ?? key.replace(/\s+/g, "-");
};

type SortOrder = "asc" | "desc" | "";

function base64url(str: string) {
  const b64 = btoa(unescape(encodeURIComponent(str)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const WebsiteTech: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const { filters, setFilters } = useFilterTechStore();

  const [lazyParams, setLazyParams] = useState({
    sortBy: "name",
    sortOrder: "asc" as SortOrder,
    limit: 50,
    page: 1,
    filters: {} as Record<string, unknown>,
  });

  const {
    data: Pages,
    isLoading,
    isError,
    error,
    refetch,
  } = usePages(PAGE_TYPES.tech);

  const createMut = useCreatePage();
  const updateMut = useUpdatePage();
  const deleteMut = useDeletePage();

  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingImage, setEditingImage] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  // + estados e helpers (coloque junto aos outros useState/useRef):
  type StepStatus = "pending" | "done" | "error";
  type Step = { key: string; label: string; status: StepStatus };

  const [isCreating, setIsCreating] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);

  const EDIT_ORIGIN =
    window.location.hostname === "localhost"
      ? "http://localhost:5173"
      : "https://www.nimber.pt";

  const initSteps = (techName: string) => {
    setSteps([
      {
        key: "createPage",
        label: `Criando página "${techName}"`,
        status: "pending",
      },
      {
        key: "createSections",
        label: "Criando seções (Header, S1, S2)",
        status: "pending",
      },
      {
        key: "createHeaderComponents",
        label: "Criando componentes do Header",
        status: "pending",
      },
      {
        key: "createS1Components",
        label: "Criando componentes do S1",
        status: "pending",
      },
      {
        key: "createS2Components",
        label: "Criando componentes do S2",
        status: "pending",
      },
      { key: "finalizing", label: "Finalizando", status: "pending" },
    ]);
  };

  const setStepStatus = (key: string, status: StepStatus) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));

  const progress = steps.length
    ? Math.round(
        (steps.filter((s) => s.status === "done").length / steps.length) * 100
      )
    : 0;

  const doStep = async (key: string, fn: () => Promise<void>) => {
    try {
      await fn();
      setStepStatus(key, "done");
    } catch (e) {
      setStepStatus(key, "error");
      throw e;
    }
  };

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if (
              typeof value === "object" &&
              value !== null &&
              "constraints" in value
            ) {
              return [
                key,
                Array.isArray(value.constraints)
                  ? value.constraints[0]?.value ?? ""
                  : "",
              ];
            }
            return [key, value?.value ?? ""];
          })
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  useEffect(() => {
    if (isError && toast.current) {
      toast.current.show({
        severity: "error",
        summary: "Erro",
        detail:
          translateRegexMessage((error as Error)?.message, "pt") ||
          "Ocorreu um erro inesperado",
        life: 2000,
      });
    }
  }, [isError, error]);

  const list = Pages ?? [];

  const listFiltered = useMemo(() => {
    const nameFilter = String(lazyParams.filters?.["name"] ?? "").toLowerCase();
    if (!nameFilter) return list;
    return list.filter((p: any) =>
      (p.name ?? "").toLowerCase().includes(nameFilter)
    );
  }, [list, lazyParams.filters]);

  const listSorted = useMemo(() => {
    const { sortBy, sortOrder } = lazyParams;
    if (!sortBy || !sortOrder) return listFiltered;

    const arr = [...listFiltered];
    arr.sort((a, b) => {
      const av = (a as any)?.[sortBy];
      const bv = (b as any)?.[sortBy];

      if (sortBy === "createdAt" || sortBy === "modifiedAt") {
        const at = av ? new Date(av).getTime() : 0;
        const bt = bv ? new Date(bv).getTime() : 0;
        return sortOrder === "asc" ? at - bt : bt - at;
      }

      const as = String(av ?? "").toLowerCase();
      const bs = String(bv ?? "").toLowerCase();
      if (as < bs) return sortOrder === "asc" ? -1 : 1;
      if (as > bs) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return arr;
  }, [listFiltered, lazyParams.sortBy, lazyParams.sortOrder]);

  const totalRecords = listSorted.length;

  const pageData = useMemo(() => {
    const start = (lazyParams.page - 1) * lazyParams.limit;
    return listSorted.slice(start, start + lazyParams.limit);
  }, [listSorted, lazyParams.page, lazyParams.limit]);

  const askConfirm = (message: string, header = "Confirmar exclusão") =>
    new Promise<boolean>((resolve) => {
      confirmDialog({
        message,
        header,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Excluir",
        rejectLabel: "Cancelar",
        acceptClassName: "p-button-danger",
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });

  const onDelete = async (row: Page) => {
    const ok = await askConfirm(
      `Excluir "${row.name}"? Esta ação não pode ser desfeita.`
    );
    if (!ok) return;

    deleteMut.mutate(
      { id: row.id },
      {
        onSuccess: () =>
          toast.current?.show({
            severity: "success",
            summary: "Excluído",
            detail: `"${row.name}" foi removido.`,
            life: 1500,
          }),
        onError: (e: any) =>
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: e?.message ?? "Falha ao excluir.",
            life: 1500,
          }),
      }
    );
  };

  const qc = useQueryClient();

  const createSection = async (pageId: string, name: string, cols = 0) => {
    const { data } = await httpClient.post(`/section`, {
      data: { pageId, name, cols },
    });
    return data?.data;
  };

  const createComponent = async (input: {
    sectionId: string;
    name: string;
    type: "text" | "image";
    value: string | Record<string, unknown>;
    order: number;
    position?: string;
  }) => {
    const { data } = await httpClient.post(
      `/component`,
      { data: input },
      { timeout: 20000 }
    );
    return data?.data;
  };

  const setStepLabel = (key: string, label: string) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, label } : s)));

  async function createInBatches(
    sectionId: string,
    defs: Array<{
      name: string;
      type: "text" | "image";
      value: any;
      order: number;
      position: string;
    }>,
    stepKey: string,
    batchSize = 3,
    pauseMs = 120
  ) {
    const total = defs.length;
    let done = 0;

    for (let i = 0; i < defs.length; i += batchSize) {
      const batch = defs.slice(i, i + batchSize);

      await Promise.all(batch.map((d) => createComponent({ sectionId, ...d })));

      done += batch.length;
      setStepLabel(
        stepKey,
        `${
          steps.find((s) => s.key === stepKey)?.label.split(" (")[0]
        } (${done}/${total})`
      );

      if (pauseMs > 0 && i + batchSize < defs.length) {
        await new Promise((r) => setTimeout(r, pauseMs));
      }
    }
  }

  const toSlug = (name: string) =>
    name.trim().toLowerCase().replace(/\s+/g, "-");

  const onCreate = async () => {
    const n = newName.trim();
    if (!n) {
      toast.current?.show({
        severity: "warn",
        summary: "Nome obrigatório",
        detail: "Informe um nome para a tecnologia.",
        life: 1500,
      });
      return;
    }

    setIsCreating(true);
    initSteps(n);

    createMut.mutate(
      { name: n, type: PAGE_TYPES.tech },
      {
        onSuccess: async (res: any) => {
          try {
            setStepStatus("createPage", "done");

            const pageCreated = res?.data ?? res;
            const pageId: string = pageCreated?.id;
            if (!pageId) throw new Error("ID da página não retornado.");

            // 1) sections
            await doStep("createSections", async () => {
              const sectionHeader = await createSection(
                pageId,
                `${n}_Header`,
                0
              );
              const sectionS1 = await createSection(pageId, `${n}_S1`, 0);
              const sectionS2 = await createSection(pageId, `${n}_S2`, 0);

              const headerId: string = sectionHeader?.id;
              const s1Id: string = sectionS1?.id;
              const s2Id: string = sectionS2?.id;
              if (!headerId || !s1Id || !s2Id) {
                throw new Error(
                  "Falha ao criar uma ou mais sections (Header/S1/S2)."
                );
              }
              (onCreate as any)._ids = { headerId, s1Id, s2Id };
            });

            const { headerId, s1Id, s2Id } = (onCreate as any)._ids as {
              headerId: string;
              s1Id: string;
              s2Id: string;
            };

            const slug = toSlug(n);

            // defaults Header
            const headerDefaults = [
              {
                name: `${n}_Header_TXT01`,
                type: "text" as const,
                value: n,
                order: 1,
                position: "",
              },
              {
                name: `${n}_Header_TXT02`,
                type: "text" as const,
                value: `Short description about this ${n}. You can edit this later in the editor.`,
                order: 2,
                position: "",
              },
              {
                name: `${n}_Header_TXT03`,
                type: "text" as const,
                value: "Schedule a meeting",
                order: 3,
                position: "",
              },
              {
                name: `${n}_Header_IMG10`,
                type: "image" as const,
                value: `/technologies/${slug}.png`,
                order: 10,
                position: "",
              },
            ];
            // defaults S1
            const s1Defaults = [
              {
                name: `${n}_S1_TXT01`,
                type: "text" as const,
                value: `Benefits of ${n}?`,
                order: 1,
                position: "",
              },
              {
                name: `${n}_S1_TXT02`,
                type: "text" as const,
                value: `${n} offers unmatched flexibility and efficiency for businesses looking to build scalable, innovative solutions. Its simplicity reduces development time, while its extensive libraries and community support ensure robust and versatile applications. From machine learning to web development, ${n} empowers teams to tackle complex challenges with ease.`,
                order: 2,
                position: "",
              },
              {
                name: `${n}_S1_IMG03`,
                type: "text" as const,
                value: `01`,
                order: 3,
                position: "",
              },
              {
                name: `${n}_S1_IMG04`,
                type: "text" as const,
                value: `Faster Development`,
                order: 4,
                position: "",
              },
              {
                name: `${n}_S1_IMG05`,
                type: "text" as const,
                value: `Write less code and deploy quicker with Python’s straightforward syntax.`,
                order: 5,
                position: "",
              },
              {
                name: `${n}_S1_IMG06`,
                type: "text" as const,
                value: `01`,
                order: 6,
                position: "",
              },
              {
                name: `${n}_S1_IMG07`,
                type: "text" as const,
                value: `Faster Development`,
                order: 7,
                position: "",
              },
              {
                name: `${n}_S1_IMG08`,
                type: "text" as const,
                value: `Write less code and deploy quicker with Python’s straightforward syntax.`,
                order: 8,
                position: "",
              },
              {
                name: `${n}_S1_IMG09`,
                type: "text" as const,
                value: `01`,
                order: 9,
                position: "",
              },
              {
                name: `${n}_S1_IMG010`,
                type: "text" as const,
                value: `Faster Development`,
                order: 10,
                position: "",
              },
              {
                name: `${n}_S1_IMG11`,
                type: "text" as const,
                value: `Write less code and deploy quicker with Python’s straightforward syntax.`,
                order: 11,
                position: "",
              },
              {
                name: `${n}_S1_IMG12`,
                type: "text" as const,
                value: `01`,
                order: 12,
                position: "",
              },
              {
                name: `${n}_S1_IMG013`,
                type: "text" as const,
                value: `Faster Development`,
                order: 13,
                position: "",
              },
              {
                name: `${n}_S1_IMG14`,
                type: "text" as const,
                value: `Write less code and deploy quicker with Python’s straightforward syntax.`,
                order: 14,
                position: "",
              },
            ];
            // defaults S2
            const s2Defaults = [
              {
                name: `${n}_S2_TXT01`,
                type: "text" as const,
                value: `Customer Story`,
                order: 1,
                position: "",
              },
              {
                name: `${n}_S2_TXT02`,
                type: "text" as const,
                value: `Manage the purchasing and supply of services related to the operation of Ericsson`,
                order: 2,
                position: "",
              },
              {
                name: `${n}_S2_TXT03`,
                type: "text" as const,
                value: `Development of a software for company project and resource management, through project planning and monitoring. The project was developed using a 4 Layer canvas architecture with an internal database and several integrations with other systems to obtain data related to materials and their values and for communication with suppliers.`,
                order: 3,
                position: "",
              },
              {
                name: `${n}_S2_TXT04`,
                type: "text" as const,
                value: `Main Tech`,
                order: 4,
                position: "",
              },
              {
                name: `${n}_S2_TXT05`,
                type: "text" as const,
                value: `Process`,
                order: 5,
                position: "",
              },
              {
                name: `${n}_S2_TXT06`,
                type: "text" as const,
                value: `The Eco-Intelligent Process`,
                order: 6,
                position: "",
              },
              {
                name: `${n}_S2_IMG01`,
                type: "image" as const,
                value: `/technologies/customer-logo.png`,
                order: 1,
                position: "",
              },
              {
                name: `${n}_S2_IMG02`,
                type: "image" as const,
                value: `/icons/angular.svg`,
                order: 2,
                position: "",
              },
              {
                name: `${n}_S2_IMG03`,
                type: "image" as const,
                value: `/icons/angular.svg`,
                order: 3,
                position: "",
              },
              {
                name: `${n}_S2_IMG04`,
                type: "image" as const,
                value: `/icons/angular.svg`,
                order: 4,
                position: "",
              },
              {
                name: `${n}_S2_IMG05`,
                type: "image" as const,
                value: `/icons/angular.svg`,
                order: 5,
                position: "",
              },
              {
                name: `${n}_S2_IMG06`,
                type: "image" as const,
                value: `/icons/angular.svg`,
                order: 6,
                position: "",
              },
              {
                name: `${n}_S2_IMG07`,
                type: "image" as const,
                value: `/icons/angular.svg`,
                order: 7,
                position: "",
              },
              {
                name: `${n}_S2_IMG08`,
                type: "image" as const,
                value: `/icons/angular.svg`,
                order: 8,
                position: "",
              },
              {
                name: `${n}_S2_IMG09`,
                type: "image" as const,
                value: `/technologies/train.png`,
                order: 9,
                position: "",
              },
              {
                name: `${n}_S2_IMG10`,
                type: "image" as const,
                value: `/technologies/executive.png`,
                order: 10,
                position: "",
              },
            ];

            const createMany = (
              sectionId: string,
              defs: Array<{
                name: string;
                type: "text" | "image";
                value: any;
                order: number;
                position: string;
              }>
            ) =>
              Promise.all(
                defs.map((c) => createComponent({ sectionId, ...c }))
              );

            await doStep("createHeaderComponents", async () => {
              await createInBatches(
                headerId,
                headerDefaults,
                "createHeaderComponents",
                3
              );
            });

            await doStep("createS1Components", async () => {
              await createInBatches(s1Id, s1Defaults, "createS1Components", 3);
            });

            await doStep("createS2Components", async () => {
              await createInBatches(s2Id, s2Defaults, "createS2Components", 3);
            });

            await doStep("finalizing", async () => {
              setNewName("");
              await qc.invalidateQueries({ queryKey: ["tech-pages"] });
            });

            toast.current?.show({
              severity: "success",
              summary: "Criado",
              detail: `Tecnologia "${n}" criada com Header, S1 e S2 padrão.`,
              life: 1800,
            });
          } catch (e: any) {
            toast.current?.show({
              severity: "error",
              summary: "Erro ao criar defaults",
              detail: e?.message ?? "Falha ao criar seção/componentes padrão.",
              life: 2500,
            });
          } finally {
            setIsCreating(false);
          }
        },
        onError: (e: any) => {
          setStepStatus("createPage", "error");
          setIsCreating(false);
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: e?.message ?? "Falha ao criar a página.",
            life: 2000,
          });
        },
      }
    );
  };

  const onSaveUpdate = (row: Page) => {
    const name = editingName.trim();
    const image = editingImage.trim();
    const description = editingDescription.trim();

    if (!name) {
      toast.current?.show({
        severity: "warn",
        summary: "Nome obrigatório",
        detail: "Informe um nome.",
        life: 1500,
      });
      return;
    }

    updateMut.mutate(
      {
        id: row.id,
        name: name !== row.name ? name : undefined,
        image: image !== (row.image || "") ? image : undefined,
        description:
          description !== (row.description || "") ? description : undefined,
      },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditingName("");
          setEditingImage("");
          setEditingDescription("");
          toast.current?.show({
            severity: "success",
            summary: "Atualizado",
            detail: "Dados atualizados com sucesso.",
            life: 1500,
          });
        },
        onError: (e: any) =>
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: e?.message ?? "Falha ao salvar.",
            life: 1500,
          }),
      }
    );
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingImage("");
    setEditingDescription("");
  };

  const goEdit = (row: Page) => {
    const slug = toSlug(row.name ?? "");
    const raw = getSessionStorageItem("auth-session-storage");
    if (!raw) {
      alert("Sem sessão válida para editar.");
      return;
    }
    const parsed = JSON.parse(raw);
    const st = parsed?.state ?? {};
    if (!st.accessToken) {
      alert("Sem token para editar.");
      return;
    }
    const payload = {
      accessToken: st.accessToken,
      refreshToken: st.refreshToken,
      expiresAt: st.expiresAt,
      userPermissions: st.userPermissions,
    };
    const tok = base64url(JSON.stringify(payload));

    const url = `${EDIT_ORIGIN}/edit/technologies/${encodeURIComponent(
      slug
    )}#tok=${tok}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (isLoading) return <Spinner />;

  return (
    <div>
      <div className="flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">{translateText("Technologies", "pt")}</h2>

        <div className="flex gap-2">
          <span className="p-input-icon-left">
            <input
              className="p-inputtext p-component"
              style={{ minWidth: 280 }}
              placeholder={translateText("New technology", "pt")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCreate();
              }}
            />
          </span>
          <button
            className="p-button p-component p-button-sm"
            onClick={onCreate}
            disabled={createMut.isPending || isCreating}
          >
            {createMut.isPending || isCreating ? (
              translateText("Saving…", "pt")
            ) : (
              <i className="pi pi-plus" />
            )}
          </button>
        </div>
      </div>

      <DataTable
        value={pageData}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={totalRecords}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: String(e.sortField ?? "name"),
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        rowsPerPageOptions={[10, 25, 50]}
        dataKey="id"
        header={null}
      >
        <Column
          field="name"
          header={translateText("Name", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
          body={(row: Page) =>
            editingId === row.id ? (
              <input
                className="p-inputtext p-component w-full"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                placeholder="Nome da tecnologia"
              />
            ) : (
              row.name
            )
          }
        />

        <Column
          field="image"
          header={translateText("Image", "pt")}
          body={(row: Page) =>
            editingId === row.id ? (
              <input
                className="p-inputtext p-component w-full"
                value={editingImage}
                onChange={(e) => setEditingImage(e.target.value)}
                placeholder="URL da imagem"
              />
            ) : row.image ? (
              <img
                src={row.image}
                alt={row.name}
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/placeholder-image.png";
                }}
              />
            ) : (
              <span className="text-500">-</span>
            )
          }
          style={{ width: "8rem" }}
        />

        <Column
          field="description"
          header={translateText("Description", "pt")}
          body={(row: Page) =>
            editingId === row.id ? (
              <textarea
                className="p-inputtext p-component w-full"
                value={editingDescription}
                onChange={(e) => setEditingDescription(e.target.value)}
                placeholder="Descrição da tecnologia"
                rows={2}
                style={{ resize: "vertical", minHeight: "60px" }}
              />
            ) : (
              <div title={row.description} style={{ maxWidth: "200px" }}>
                {row.description ? (
                  <span className="text-overflow-ellipsis">
                    {row.description}
                  </span>
                ) : (
                  <span className="text-500">-</span>
                )}
              </div>
            )
          }
          style={{ width: "16rem" }}
        />

        <Column
          header={translateText("Slug", "pt")}
          body={(row: Page) => toSlug(row.name ?? "")}
          style={{ width: "18rem" }}
        />

        <Column
          field="createdAt"
          header={translateText("Created at", "pt")}
          sortable
          body={(row: Page) =>
            row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"
          }
          style={{ width: "14rem" }}
        />

        <Column
          field="modifiedAt"
          header={translateText("Updated at", "pt")}
          sortable
          body={(row: Page) =>
            row.modifiedAt ? new Date(row.modifiedAt).toLocaleString() : "-"
          }
          style={{ width: "14rem" }}
        />

        <Column
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
          body={(row: Page) => (
            <div className="flex gap-2 justify-content-center">
              {editingId === row.id ? (
                <>
                  <button
                    className="p-button p-component p-button-sm"
                    onClick={() => onSaveUpdate(row)}
                    disabled={updateMut.isPending}
                  >
                    {updateMut.isPending
                      ? translateText("Saving…", "pt")
                      : translateText("Save", "pt")}
                  </button>
                  <button
                    className="p-button p-button-text p-component p-button-sm"
                    onClick={onCancelEdit}
                  >
                    {translateText("Cancel", "pt")}
                  </button>
                </>
              ) : (
                <>
                  <i
                    className="pi pi-pencil cursor-pointer"
                    title={translateText("Edit", "pt")}
                    onClick={() => {
                      setEditingId(row.id);
                      setEditingName(row.name ?? "");
                      setEditingImage(row.image ?? "");
                      setEditingDescription(row.description ?? "");
                    }}
                  />
                  <i
                    className="pi pi-file-edit cursor-pointer"
                    title={translateText("Edit content", "pt")}
                    onClick={() => goEdit(row)}
                  />
                  <i
                    className="pi pi-trash cursor-pointer"
                    title={translateText("Delete", "pt")}
                    onClick={() => onDelete(row)}
                  />
                </>
              )}
            </div>
          )}
          style={{ width: "14rem" }}
        />
      </DataTable>

      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />
      <Dialog
        visible={isCreating}
        header={translateText("Creating technology", "pt")}
        modal
        closable={false}
        style={{ width: "32rem", maxWidth: "90vw" }}
        onHide={() => {}}
      >
        <div className="flex align-items-center gap-2 mb-3">
          <i className="pi pi-cog pi-spin text-500" />
          <span className="text-500">
            {translateText("Please wait…", "pt")}
          </span>
        </div>

        <ul className="p-0 m-0" style={{ listStyle: "none" }}>
          {steps.map((s) => (
            <li key={s.key} className="flex align-items-center gap-2 mb-2">
              <i
                className={
                  s.status === "done"
                    ? "pi pi-check-circle text-green-500"
                    : s.status === "error"
                    ? "pi pi-times-circle text-red-500"
                    : "pi pi-circle-on text-500"
                }
              />
              <span>{s.label}</span>
            </li>
          ))}
        </ul>

        <div className="mt-3">
          <ProgressBar value={progress} />
        </div>
      </Dialog>
    </div>
  );
};

export default WebsiteTech;
